<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { emitter } from 'shared/helpers/mitt';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { createKeyboardEventsFromLabel } from 'dashboard/components/widgets/modal/constants';
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
  CMD_SELECT_NEXT_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { uiSettings } = useUISettings();

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => toggleDropdown(true);

const currentChat = computed(() => getters.getSelectedChat.value);

const inboxHasCsat = computed(() => {
  const inboxId = currentChat.value?.inbox_id;
  const inbox = getters['inboxes/getInboxById'].value(inboxId);
  return inbox?.csatSurveyEnabled ?? false;
});

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const showAdditionalActions = computed(
  () => !isPending.value && !isSnoozed.value
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const shouldAutoNavigate = computed(() => {
  return uiSettings.value.auto_navigate_to_next_conversation || false;
});

const openSnoozeModal = () => {
  const ninja = document.querySelector('ninja-keys');
  ninja.open({ parent: 'snooze_conversation' });
};

// Resolves to whether the status actually changed
const toggleStatus = async (status, snoozedUntil, skipCsat = false) => {
  closeDropdown();
  if (isLoading.value) return false;
  isLoading.value = true;
  try {
    await store.dispatch('toggleStatus', {
      conversationId: currentChat.value.id,
      status,
      snoozedUntil,
      skipCsat,
    });
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
    return true;
  } catch (error) {
    useAlert(t('CONVERSATION.CHANGE_STATUS_FAILED'));
    return false;
  } finally {
    isLoading.value = false;
  }
};

const selectNextConversation = () => {
  if (!shouldAutoNavigate.value) {
    return;
  }
  emitter.emit(CMD_SELECT_NEXT_CONVERSATION);
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdResolveConversation = async () => {
  if (await toggleStatus(wootConstants.STATUS_TYPE.RESOLVED)) {
    selectNextConversation();
  }
};

const keyboardEvents = {
  ...createKeyboardEventsFromLabel('TOGGLE_SNOOZE_DROPDOWN', {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  }),
  ...createKeyboardEventsFromLabel('RESOLVE_CONVERSATION', {
    action: async event => {
      // Chrome on Windows treats Alt+E as a legacy shortcut for its own menu. Without
      // preventDefault the conversation resolves, and Chrome's menu opens on top of it.
      event.preventDefault();
      await toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
    },
  }),
  ...createKeyboardEventsFromLabel('RESOLVE_AND_NEXT', {
    action: async event => {
      event.preventDefault();
      if (await toggleStatus(wootConstants.STATUS_TYPE.RESOLVED)) {
        selectNextConversation();
      }
    },
  }),
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <div class="relative flex items-center justify-end resolve-actions">
    <div
      class="rounded-lg shadow outline-1 outline"
      :class="!showOpenButton ? 'outline-n-container' : 'outline-transparent'"
    >
      <Button
        v-if="isOpen"
        :label="t('CONVERSATION.HEADER.RESOLVE_ACTION')"
        size="sm"
        color="blue"
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        :disabled="isLoading"
        @click="onCmdResolveConversation"
      />
      <Button
        v-else-if="isResolved"
        :label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
        size="sm"
        color="slate"
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        :disabled="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-else-if="showOpenButton"
        :label="t('CONVERSATION.HEADER.OPEN_ACTION')"
        size="sm"
        color="slate"
        :is-loading="isLoading"
        :disabled="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-if="showAdditionalActions"
        ref="arrowDownButtonRef"
        icon="i-lucide-chevron-down"
        :disabled="isLoading"
        size="sm"
        class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
        :color="isOpen ? 'blue' : 'slate'"
        trailing-icon
        @click="openDropdown"
      />
    </div>
    <div
      v-if="showActionsDropdown"
      v-on-clickaway="closeDropdown"
      class="dropdown-pane dropdown-pane--open left-auto top-full mt-0.5 ltr:right-0 rtl:left-0 max-w-[12.5rem] min-w-[9.75rem]"
    >
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL')"
            ghost
            slate
            sm
            start
            icon="i-lucide-alarm-clock-minus"
            class="w-full"
            @click="() => openSnoozeModal()"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING')"
            ghost
            slate
            sm
            start
            icon="i-lucide-circle-dot-dashed"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.PENDING)"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="isOpen && inboxHasCsat">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.RESOLVE_WITHOUT_CSAT')"
            ghost
            slate
            sm
            start
            icon="i-lucide-circle-check"
            class="w-full"
            @click="
              () => toggleStatus(wootConstants.STATUS_TYPE.RESOLVED, null, true)
            "
          />
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.dropdown-pane {
  .dropdown-menu__item {
    @apply mb-0;
  }
}
</style>
